import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from '@heroicons/react/24/outline';
import { ShoppingCartIcon as ShoppingCartOutlineIcon } from '@heroicons/react/24/outline';
import { ShoppingCartIcon as ShoppingCartSolidIcon } from '@heroicons/react/24/solid';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { toast } from 'react-hot-toast';
import { defaultCartSize } from '../utils/productSize';
import { discountPercent, hasDiscount } from '../utils/productPrice';
import ProductPrice from './ProductPrice';
import { HeartIcon as HeartSolidIcon, HeartIcon as HeartOutlineIcon } from '@heroicons/react/24/solid';
import { TrashIcon } from '@heroicons/react/24/outline';
const ProductCard = ({ product, onAddToCartFromWishlist, isWishlisted, toggleWishlist, isWishlist, onDelete }) => {
  const [imgIdx, setImgIdx] = useState(0);
  const { addToCart, isInCart } = useCart();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const navigate = useNavigate();

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    if (!product || !product._id) {
      toast.error('Invalid product data');
      return;
    }
    setIsAddingToCart(true);
    try {
      const result = await addToCart(product, 1, defaultCartSize(product), 'Default');
      if (result) {
        toast.success('Item added to cart successfully');
        if (onAddToCartFromWishlist) {
          onAddToCartFromWishlist(product._id);
        }
      } else {
        toast.error('Failed to add item to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart');
    } finally {
      setIsAddingToCart(false);
    }
  };

  // Calculate average rating from reviews if available
  const reviews = product.reviews || [];
  const averageRating = reviews.length > 0 ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length) : 0;
  const roundedRating = Math.round(averageRating * 10) / 10;

  return (
    <div
      className="relative bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg hover:shadow-2xl transition-transform duration-300 hover:scale-105 flex flex-col items-center p-2 sm:p-3 md:p-4 lg:p-6 group cursor-pointer w-full max-w-[420px] mx-auto"
      onClick={() => navigate(`/product/${product._id}`)}
    >
      {/* Top Right Icon: Heart or Delete */}
      {isWishlist ? (
        <button
          onClick={e => { e.stopPropagation(); onDelete && onDelete(product._id); }}
          className="absolute top-2 sm:top-4 right-2 sm:right-4 z-20 bg-white rounded-full p-1.5 sm:p-2 shadow-md pointer-events-auto"
          aria-label="Remove from wishlist"
        >
         <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      ) : (
        <button
          onClick={e => { 
            e.stopPropagation(); 
            console.log('Heart button clicked for product:', product._id, product.name);
            console.log('Current wishlist state:', isWishlisted ? isWishlisted(product._id) : 'isWishlisted function not available');
            toggleWishlist(product); 
          }}
          className="absolute top-2 sm:top-4 right-2 sm:right-4 z-20 bg-white rounded-full p-1.5 sm:p-2 shadow-md pointer-events-auto"
          aria-label="Add to wishlist"
        >
          {isWishlisted && isWishlisted(product._id) ? (
            <HeartSolidIcon className="w-5 h-5 sm:w-7 sm:h-7 text-pink-600 animate-pulse" />
          ) : (
            <HeartOutlineIcon className="w-5 h-5 sm:w-7 sm:h-7 text-gray-400" />
          )}
        </button>
      )}
      {/* Organic/Circle Image with hover animation */}
      <div className="relative w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 mb-2 sm:mb-3 md:mb-4 flex items-center justify-center z-10">
        <div className="card-image-container w-full h-full bg-pink-50 border-4 border-pink-100 transition-all duration-300 overflow-hidden flex items-center justify-center rounded-[45%_55%_60%_40%_/_60%_40%_55%_45%] group-hover:rounded-[55%_45%_40%_60%_/_40%_60%_45%_55%] group-hover:border-pink-400">
          <img 
            src={product.images?.[imgIdx] || product.rentalImages?.[imgIdx] || 'https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=400&q=80'} 
            alt={product.name} 
            className="w-full h-full object-cover image-loading" 
            onLoad={(e) => {
              e.target.classList.remove('image-loading');
              e.target.classList.add('image-loaded');
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div 
            className="absolute inset-0 image-fallback"
            style={{ display: 'none' }}
          >
            <div className="text-center">
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-pink-600 text-xl font-bold">{product.name.charAt(0)}</span>
              </div>
              <p className="text-gray-600 text-sm">{product.name}</p>
            </div>
          </div>
        </div>
        {hasDiscount(product) && (
          <span className="absolute top-4 left-4 bg-green-100 text-green-700 text-xs px-3 py-1 rounded-full font-semibold z-20">
            {discountPercent(product)}% OFF
          </span>
        )}
      </div>
      {/* Brand/Category Tag */}
      <span className="text-xs bg-pink-100 text-pink-600 rounded-full px-2 sm:px-3 py-1 mb-1 sm:mb-2">{product.brand || product.categoryName}</span>
      {/* Name */}
      <h3 className="font-bold text-xs sm:text-sm md:text-lg text-center mb-1 z-10 line-clamp-2">{product.name}</h3>
      {/* Description */}
      <p className="text-gray-500 text-xs mb-1 sm:mb-2 z-10 line-clamp-2">{product.description}</p>
      {/* Price */}
      <div className="flex flex-col items-center gap-1 mb-1 sm:mb-2 z-10">
        <ProductPrice
          product={product}
          saleClassName="text-pink-600 font-bold text-sm sm:text-base md:text-lg"
          retailClassName="text-gray-400 line-through text-xs sm:text-sm md:text-base"
        />
        {product.isRental && (
          <span className="text-xs text-pink-500 font-semibold">Deposit: ₹{product.securityDeposit}</span>
        )}
      </div>
      {/* Rating */}
      <div className="flex items-center justify-center mb-1 sm:mb-2 z-10">
        {[...Array(5)].map((_, i) => (
          <StarIcon key={i} className={`h-2 w-2 sm:h-3 sm:w-3 md:h-4 md:w-4 ${i < Math.round(averageRating) ? 'text-yellow-400' : 'text-gray-300'}`} />
        ))}
        <span className="ml-1 sm:ml-2 text-xs text-gray-600 font-semibold">
          {reviews.length > 0 ? `${roundedRating} (${reviews.length})` : 'No reviews'}
        </span>
      </div>
      {/* Add to Cart Button */}
      <div className="flex justify-center w-full mt-1 sm:mt-2 z-10">
        <button
          onClick={handleAddToCart}
          disabled={isAddingToCart || isInCart(product._id)}
          className={`w-full rounded-full flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 font-semibold shadow transition-transform duration-200 group-hover:scale-105 text-xs sm:text-sm md:text-base
            ${isInCart(product._id)
              ? 'bg-gray-100 text-pink-500 border border-pink-200 cursor-not-allowed'
              : 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:from-pink-600 hover:to-purple-600'}
          `}
        >
          {isInCart(product._id) ? (
            <>
              <ShoppingCartSolidIcon className="h-4 w-4 sm:h-5 sm:w-5 text-pink-500" /> In Cart
            </>
          ) : isAddingToCart ? (
            <>
              <ShoppingCartOutlineIcon className="h-4 w-4 sm:h-5 sm:w-5" /> Adding...
            </>
          ) : (
            <>
              <ShoppingCartOutlineIcon className="h-4 w-4 sm:h-5 sm:w-5" /> Add to Cart
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ProductCard; 